import { getImageAttrs } from '~/modules/images';
import { RevampCategoryPills } from './RevampCategoryPills';
import { RevampPublishDateLine } from './RevampPublishDate';
import { REVAMP_FLEXI_HERO_SIZES } from '../util/revamp-image-sizes';
import type { RevampHeaderCategory } from '../type';

export interface RevampFlexiTitleSectionProps {
    /** The article's existing TopBanner image, run tall beside the copy. */
    imageUrl: string;
    /** The banner's own alt text. Empty when it has none — the h1 already names the article. */
    alt: string;
    /** Article headline, set in the Gestura display face. */
    title: string;
    /** Professional credited in the "Sponsored by" line. */
    sponsoredBy?: string;
    categories: RevampHeaderCategory[];
    /** Article teaser (introText). No drop cap in this tier, unlike the standard one. */
    teaser?: string;
    publishDate?: string;
}

/**
 * Flexi editorial title section (Figma 2738:57368) — the banner photo held to
 * the left column with the sponsor line, display headline, category pills and
 * teaser stacked in a centred right column. Replaces the standard author rail
 * + header + teaser + hero stack for flexi-tier articles, and unlike the
 * premium section it sits below a normal solid header. Sizes in rem at the
 * 1920 artboard (÷16), base + md: only, matching the article-revamp siblings.
 */
export const RevampFlexiTitleSection = ({
    imageUrl,
    alt,
    title,
    sponsoredBy,
    categories,
    teaser,
    publishDate,
}: RevampFlexiTitleSectionProps) => {
    return (
        <section className="w-full bg-white md:px-12">
            {/* 114rem = the 120rem container less its two 3rem gutters, so above
                qhd the columns stay on the centred container rather than
                spreading with the viewport. */}
            <div className="mx-auto flex w-full max-w-[114rem] flex-col gap-8 md:grid md:min-h-[53.75rem] md:grid-cols-[46.625rem_1fr] md:gap-x-[11.125rem]">
                {/* The grid row stretches this cell to the taller of the two
                    columns, so the photo is cropped to fill rather than fixed
                    to the design's 746x860. Mobile keeps that ratio. */}
                <div className="relative aspect-[746/860] w-full overflow-hidden rounded-tile md:aspect-auto">
                    <img
                        {...getImageAttrs(imageUrl, {
                            sizes: REVAMP_FLEXI_HERO_SIZES,
                        })}
                        alt={alt}
                        fetchPriority="high"
                        decoding="async"
                        className="absolute inset-0 size-full object-cover"
                    />
                </div>
                <div className="flex flex-col justify-center gap-10 px-4.5 md:gap-[13.9375rem] md:px-0">
                    <div className="flex flex-col gap-8 md:gap-[2.75rem]">
                        <div className="flex flex-col gap-3 md:pt-14">
                            {/* The design's #9a9a9a only reaches 2.8:1 on
                                white; #767676 is the darkest grey that still
                                reads as muted at 4.5:1 (WCAG AA). */}
                            {sponsoredBy && (
                                <p className="m-0 font-sans text-sm font-medium tracking-[-0.05em] text-[#767676] md:text-[0.9375rem] md:leading-[1.22]">
                                    Sponsored by {sponsoredBy}
                                </p>
                            )}
                            {/* Follows the sponsor line, sharing its muted
                                treatment, so the credits read as one block
                                above the headline. */}
                            <RevampPublishDateLine
                                publishDate={publishDate}
                                className="font-sans text-sm font-medium tracking-[-0.05em] text-[#767676] md:text-[0.9375rem] md:leading-[1.22]"
                            />
                            <h1 className="m-0 font-gestura text-[2.25rem] leading-none font-normal text-black md:max-w-[49.75rem] md:text-[4rem]">
                                {title}
                            </h1>
                        </div>
                        <RevampCategoryPills
                            categories={categories}
                            className="md:max-w-[36.9375rem]"
                        />
                    </div>
                    {teaser && (
                        <p className="m-0 font-serif text-[1.125rem] leading-[1.8] text-black md:max-w-[50.5rem] md:text-[1.25rem] md:leading-[2rem]">
                            {teaser}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
};
